import React, { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useInstitute } from '../../contexts/InstituteContext';
import { useAccess } from '../../contexts/AccessContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import {
  LayoutDashboard,
  BookOpen,
  Trophy,
  FileText,
  Briefcase,
  Building2,
  Bookmark,
  User,
  Settings,
  Moon,
  Sun,
  LogOut,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Users,
  BarChart3,
  History,
  CheckCircle2,
  Calendar,
  ClipboardList,
  Palette,
  Video,
  Globe,
  IndianRupee,
  Cast,
  PlayCircle,
  ShieldAlert,
  Shield,
  Code2,
  Wallet,
  Award,
  Mail,
  Bell,
  HelpCircle,
  ChevronsLeft,
  ChevronLeft,
  BarChart2,
  X,
  Home,
  Play,
  CheckSquare,
  Pin,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const STUDENT_TRACK_ITEMS = [
  { name: 'Course Management', href: '/student-track/course-management', icon: BookOpen },
  { name: 'Program Management', href: '/admin/programs', icon: Building2 },
  { name: 'Mentor Management', href: '/student-track/instructor-management', icon: Users },
  { name: 'Student Management', href: '/student-track/student-management', icon: Users },
  { name: 'Schedule Management', href: '/student-track/schedule-management', icon: Calendar },
  { name: 'Assessments', href: '/admin/assessments', icon: ClipboardList },
  { name: 'Assignment Management', href: '/instructor/assignments/portal', icon: ClipboardList },
  { name: 'Recording Management', href: '/admin/recording-management', icon: Video },
  { name: 'Mark Attendance', href: '/instructor/attendance', icon: CheckCircle2 },
  { name: 'Attendance History', href: '/instructor/attendance-history', icon: History },
];

const SUPERADMIN_ITEMS = [
  { name: 'Institutes', href: '/superadmin/institutes', icon: Building2 },
  { name: 'Subscription Plans', href: '/superadmin/subscription-plans', icon: FileText },
  
  { name: 'Referral Settings', href: '/admin/referral-settings', icon: Award },
];

const INSTITUTE_ADMIN_ITEMS = [
  { name: 'Institute Analytics', href: '/institute/analytics', icon: BarChart3 },
  { name: 'Branding', href: '/institute/branding', icon: Palette },
];

const INSTRUCTOR_TRACK_ITEMS = [
  { name: 'Mentor Dashboard', href: '/instructor/dashboard', icon: LayoutDashboard },
  { name: 'Course Management', href: '/instructor/courses', icon: BookOpen },
  { name: 'Program Management', href: '/instructor/programs', icon: Building2 },
  { name: 'Student Management', href: '/instructor/students', icon: Users },
  { name: 'Batch schedule', href: '/instructor/batches', icon: Calendar },
  { name: 'Assessments Management', href: '/instructor/assessments', icon: ClipboardList },
  // { name: 'Assignment (Old)', href: '/instructor/assignments', icon: ClipboardList },
  { name: 'Assignment Management', href: '/instructor/assignments/portal', icon: ClipboardList },
  // { name: 'Exam Report', href: '/instructor/exam-report', icon: FileText },
  { name: 'Code Lab', href: '/code-lab', icon: Code2 },
  { name: 'Mark Attendance', href: '/instructor/attendance', icon: CheckCircle2 },
  { name: 'Attendance History', href: '/instructor/attendance-history', icon: History },
  { name: 'Recording Management', href: '/instructor/recordings', icon: Video },
  // { name: 'Reports & Analytics', href: '/instructor/reports', icon: BarChart3 },
];
import logo from '../../assets/navipro_logo.png';
import favicon from '../../assets/favicon.png';

const Sidebar = ({ isOpen, isCollapsed, setIsCollapsed, onNavItemClick, onLogout, unreadNotifications = 0 }) => {
  const { user, logout } = useAuth();
  const handleLogout = onLogout || logout;
  const { institute, branding } = useInstitute();
  const { loading: accessLoading, hasModule, isPlatformSuperadmin } = useAccess();
  const { hasPermission } = usePermissions();
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [superadminOpen, setSuperadminOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [studentManagementOpen, setStudentManagementOpen] = useState(false);
  const [examManagementOpen, setExamManagementOpen] = useState(false);
  const [systemAdministrationOpen, setSystemAdministrationOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(true);
  const [studentPendingCounts, setStudentPendingCounts] = useState({ assignments: 0, exams: 0, assessments: 0 });
  const [instituteSettings, setInstituteSettings] = useState({ showPredefinedCourses: true });

  // Load institute settings
  useEffect(() => {
    const loadInstituteSettings = async () => {
      if (user?.role === 'admin' && user?.institute_id) {
        try {
          const response = await api.getInstituteSettings();
          if (response.data.success) {
            setInstituteSettings({
              showPredefinedCourses: response.data.data.show_predefined_courses ?? true
            });
          }
        } catch (error) {
          console.error('Failed to load institute settings:', error);
        }
      }
    };

    loadInstituteSettings();
  }, [user]);

  useEffect(() => {
    if (user?.role !== 'student') return;

    let canceled = false;
    const fetchCounts = async () => {
      try {
        // Only fetch assessment counts on relevant pages, not on wallet page
        const currentPath = window.location.pathname;
        const isWalletPage = currentPath === '/wallet' || currentPath.includes('/wallet');
        
        if (isWalletPage) {
          setStudentPendingCounts({ assignments: 0, exams: 0, assessments: 0 });
          return;
        }

        // Try reading from sessionStorage cache first
        const cached = sessionStorage.getItem('student_pending_counts');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            // Cache is valid for 60 seconds (1 minute)
            if (Date.now() - parsed.timestamp < 60 * 1000) {
              setStudentPendingCounts({
                assignments: parsed.assignments,
                exams: parsed.exams,
                assessments: parsed.assessments
              });
              return;
            }
          } catch (e) {
            // Ignore parse errors and fallback to API calls
          }
        }

        // Single lightweight API call instead of 3 heavy ones
        const response = await api.get('/student/dashboard/sidebar-counts');

        if (canceled) return;

        if (response?.data?.success) {
          const counts = response.data.data;
          setStudentPendingCounts({
            assignments: counts.assignments || 0,
            exams: counts.exams || 0,
            assessments: counts.assessments || 0
          });

          // Store counts in sessionStorage cache
          sessionStorage.setItem('student_pending_counts', JSON.stringify({
            assignments: counts.assignments || 0,
            exams: counts.exams || 0,
            assessments: counts.assessments || 0,
            timestamp: Date.now()
          }));
        }
      } catch (err) {
        if (!canceled) setStudentPendingCounts({ assignments: 0, exams: 0, assessments: 0 });
      }
    };

    fetchCounts();
    return () => { canceled = true; };
  }, [user?.role, user?.id]);

  const STUDENT_TRACK_ITEMS = [
    { name: t('sidebar.courseManagement'), href: '/student-track/course-management', icon: BookOpen, permission: 'course_access' },
    { name: t('sidebar.batchManagement'), href: '/admin/programs', icon: Building2, permission: 'batch_access' },
    { name: t('sidebar.runwayManagement'), href: '/admin/runways', icon: Building2, permission: 'runway_access' },
    { name: t('sidebar.instructorManagement'), href: '/student-track/instructor-management', icon: Users, permission: 'user_access' },
    { name: t('sidebar.batchSchedule'), href: '/student-track/schedule-management', icon: Calendar, permission: 'batch_access' },
    { name: 'Question Manager', href: '/admin/question-manager', icon: FileText, permission: 'questions_access' },
    // { name: 'Exam Management', href: '/admin/assignments/portal', icon: ClipboardList, permission: 'exam_access' },
    { name: 'Assignment Management', href: '/admin/assessments', icon: ClipboardList, permission: 'exam_access' },
    { name: 'Recording Management', href: '/admin/recording-management', icon: Video },
    { name: t('sidebar.markAttendance'), href: '/admin/attendance', icon: CheckCircle2, permission: 'attendance_access' },
    { name: t('sidebar.attendanceHistory'), href: '/admin/attendance-history', icon: History, permission: 'attendance_access' },
    // { name: 'Certificate Management', href: '/admin/course-completion-certificate', icon: Award },
    { name: 'Course List', href: '/admin/course-list', icon: BookOpen, permission: 'predefined_course' },
  ];

  const SUPPORT_ITEMS = [
  { name: 'Registered Users', href: '/admin/exam-users', icon: Users },
];

const STUDENT_MANAGEMENT_SUBITEMS = [
    { name: 'Student Management', href: '/student-track/student-management', icon: Users, permission:'user_access' },
    { name: 'Registered Users', href: '/admin/exam-users', icon: Users },
  ];

const   USER_MANAGEMENT = [
    { name: 'Support Users', href: '/admin/support-users', icon: Shield },
    { name: 'Institutes', href: '/superadmin/institutes', icon: Building2 },
  ];

  const EXAM_MANAGEMENT_SUBITEMS = [
    { name: 'Exam Management', href: '/admin/assignments/portal', icon: ClipboardList, permission:'exam_access' },
    { name: 'Registered Exam Users Marks', href: '/admin/paid-exam-marks', icon: Trophy, },
  ];

  const PAYMENTS_ITEMS = [
    { name: t('sidebar.payments'), href: '/student/payments', icon: IndianRupee, permission: 'payment_access' },
    { name: t('sidebar.paymentHistory'), href: '/student/payments', icon: History, permission: 'payment_access' },
  ];

  const ADMIN_PAYMENTS_ITEMS = [
    { name: t('sidebar.paymentManagement'), href: '/admin/payments', icon: IndianRupee },
    { name: t('sidebar.studentPaymentSupport'), href: '/admin/student-payment-support', icon: Users },
    { name: 'Subscription Payments', href: '/admin/institute-subscription-payments', icon: IndianRupee },
    { name: 'Subscriptions', href: '/admin/institute-subscription-plans', icon: Calendar },
    { name: 'Subscription Plans', href: '/superadmin/subscription-plans', icon: FileText },

    { name: 'Referral Settings', href: '/admin/referral-settings', icon: Award },
    // { name: 'Course Purchase', href: '/admin/course-purchase', icon: BookOpen },
  ];

  // const SUPERADMIN_ITEMS = [
  //   { name: t('sidebar.institutes'), href: '/superadmin/institutes', icon: Building2 },
    
    
  // ];

  const INSTITUTE_ADMIN_ITEMS = [
    { name: t('sidebar.instituteAnalytics'), href: '/institute/analytics', icon: BarChart3 },
    { name: t('sidebar.branding'), href: '/institute/branding', icon: Palette },
  ];

  const INSTRUCTOR_TRACK_ITEMS = [
    { name: t('sidebar.instructorDashboard'), href: '/instructor/dashboard', icon: LayoutDashboard },
    { name: t('sidebar.courseManagement'), href: '/instructor/courses', icon: BookOpen, permission: 'course_access' },
    { name: t('sidebar.batchManagement'), href: '/instructor/programs', icon: Building2, permission: 'batch_access' },
    { name: t('sidebar.runwayManagement'), href: '/instructor/runways', icon: Building2, permission: 'runway_access' },
    { name: t('sidebar.studentManagement'), href: '/instructor/students', icon: Users, permission: 'user_access' },
    { name: t('sidebar.batchSchedule'), href: '/instructor/batches', icon: Calendar, permission: 'batch_access' },
    { name: 'Question Manager', href: '/instructor/question-manager', icon: FileText, permission: 'questions_access' },
    // { name: 'Assignment (Old)', href: '/instructor/assignments', icon: ClipboardList },
    { name: 'Exam Management', href: '/instructor/assignments/portal', icon: ClipboardList, permission: 'exam_access' },
    // { name: 'Exam Report', href: '/instructor/exam-report', icon: FileText, permission: 'exam_access' },
    { name: 'Assignment Management', href: '/instructor/assessments', icon: ClipboardList, permission: 'exam_access' },
    { name: t('sidebar.codeLab'), href: '/code-lab', icon: Code2 },
    { name: t('sidebar.markAttendance'), href: '/instructor/attendance', icon: CheckCircle2, permission: 'attendance_access' },
    { name: t('sidebar.attendanceHistory'), href: '/instructor/attendance-history', icon: History, permission: 'attendance_access' },
    { name: 'Recording Management', href: '/instructor/recordings', icon: Video },
    // { name: 'Reports & Analytics', href: '/instructor/reports', icon: BarChart3 },
  ];
  const codeLabIndex = INSTRUCTOR_TRACK_ITEMS.length;

  const adminNavigation = [
    { name: t('dashboard'), href: '/admin/dashboard', icon: LayoutDashboard },
    // { name: t('sidebar.managePanel'), href: '/admin-panel', icon: Settings },
    { name: t('profile.profile'), href: '/profile', icon: User },
    { name: t('settings.settings'), href: '/settings', icon: Settings },
    { name: 'Email Templates', href: '/superadmin/email-templates', icon: Mail },
    { name: t('Notifications'), href: '/notifications-center', icon: Bell },
  ];

  const commonNavigation = [
    
  ];

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const instructorNavigation = [
    { name: t('sidebar.instructorDashboard'), href: '/instructor/dashboard', icon: LayoutDashboard },
    // { name: 'Reports & Analytics', href: '/instructor/reports', icon: BarChart3 },
  ];
  const studentNavigation = [
    { name: t('dashboard'), href: '/dashboard', icon: LayoutDashboard },
    { name: t('courses'), href: '/courses', icon: BookOpen },
    // { name: 'My Courses', href: '/student/courses', icon: BookOpen },
    { name: t('sidebar.liveClasses'), href: '/live-classes', icon: Cast },
    // { name: 'Recordings', href: '/recordings', icon: PlayCircle },
    { name: t('sidebar.testsQuiz'), href: '/tests', icon: ShieldAlert },
    { name: 'Topic Exams', href: '/assignments', icon: FileText },
    { name: 'Assignments', href: '/student/assessments', icon: ClipboardList },
    { name: t('sidebar.codeLab'), href: '/code-lab', icon: Code2 },
    { name: t('sidebar.bookmarks'), href: '/bookmarks', icon: Bookmark },
    // { name: 'Certificate Management', href: '/certificate-view', icon: Award },
    { name: 'My Wallet', href: '/wallet', icon: Wallet },
    { name: 'Certificate Management', href: '/certificate-view', icon: Award },
    { name: t('profile.profile'), href: '/profile', icon: User },
  ];

  // Filter navigation for paid exam users - only show Tests/Quiz
  const getFilteredStudentNavigation = () => {
    if (user?.type === 'paid_exam_user') {
      return studentNavigation.filter(item => item.href === '/tests');
    }
    return studentNavigation;
  };

  const studentTourIdByHref = useMemo(() => ({
    '/dashboard': 'nav-dashboard',
    '/courses': 'nav-courses',
    '/live-classes': 'nav-live-classes',
    '/recordings': 'nav-recordings',
    '/tests': 'nav-tests',
    '/student/assessments': 'nav-assignments',
    '/code-lab': 'nav-codelab',
    '/bookmarks': 'nav-bookmarks',
    '/wallet': 'nav-wallet',
    '/certificate-view': 'nav-certificates',
    '/profile': 'nav-profile',
  }), []);

  const isActive = (href) => {
    // Handle exact match
    if (location.pathname === href) {
      return true;
    }

    // Check for active parent menu from sessionStorage (for inner pages)
    const activeParentMenu = sessionStorage.getItem('activeParentMenu');
    if (activeParentMenu && href === activeParentMenu) {
      return true;
    }

    // Automatic parent menu detection for dynamic routes
    const pathname = location.pathname;

    // Question Management routes - highlight Question Manager
    if (href === '/instructor/question-manager' && (
      pathname.startsWith('/instructor/questions') ||
      pathname.startsWith('/instructor/assignments/') && pathname.includes('/questions/')
    )) {
      return true;
    }

    if (href === '/admin/question-manager' && (
      pathname.startsWith('/admin/questions') ||
      pathname.startsWith('/admin/assignments/') && pathname.includes('/questions/')
    )) {
      return true;
    }

    // Assignment Management routes
    if (href === '/instructor/assignments/portal' && pathname.startsWith('/instructor/assignments/')) {
      return true;
    }

    if (href === '/admin/assignments/portal' && pathname.startsWith('/admin/assignments/')) {
      return true;
    }

    // Exam Management routes
    if (href === '/instructor/exam-report' && pathname.startsWith('/instructor/exam-report')) {
      return true;
    }

    return false;
  };

  // Check if any sub-item in a dropdown is active
  const isAnySubItemActive = (subItems) => {
    return subItems.some(item => isActive(item.href));
  };

  // Auto-expand dropdowns when any sub-item is active
  useLayoutEffect(() => {
    if (isAnySubItemActive(visibleStudentManagementSubItems)) {
      setStudentManagementOpen(true);
    }
    if (isAnySubItemActive(USER_MANAGEMENT)) {
      setSystemAdministrationOpen(true);
    }
    if (isAnySubItemActive(visibleExamManagementSubItems)) {
      setExamManagementOpen(true);
    }
    if (isAnySubItemActive(ADMIN_PAYMENTS_ITEMS)) {
      setPaymentsOpen(true);
    }
    if (isAnySubItemActive(SUPPORT_ITEMS)) {
      setSupportOpen(true);
    }
  }, [location.pathname]);
  const isInstituteAdmin = user?.role === 'admin' && !isPlatformSuperadmin;
  const headerTitle = isInstituteAdmin ? (institute?.name || user?.full_name || 'Institute') : 'Navipro Nxt';
  const logoHref = (location.pathname.startsWith('/instructor') || (location.pathname.startsWith('/curriculum-plan') && user?.role === 'instructor'))
    ? '/instructor/dashboard'
    : (user?.role === 'support' ? '/admin/exam-users' : ((user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'super_admin') ? '/admin/dashboard' : (user?.type === 'paid_exam_user' ? '/tests' : '/dashboard')));

  const moduleByHref = useMemo(() => ({
    '/student-track/course-management': 'course_management',
    '/admin/programs': 'program_management',
    '/admin/runways': 'runway_management',
    '/student-track/instructor-management': 'instructor_management',
    '/student-track/student-management': 'student_management',
    '/student-track/schedule-management': 'schedule_management',
    '/admin/assessments': 'assessments',
    '/admin/assignments/portal': 'assignments',
    '/instructor/assignments/portal': 'assignments',
    '/admin/question-manager': 'assignments',
    '/admin/recording-management': 'recordings',
    '/instructor/assignments/portal': 'assignments',
    '/instructor/attendance': 'attendance',
    '/instructor/attendance-history': 'attendance',
  }), []);

  const canSeeModule = (moduleKey) => {
    if (!moduleKey) return true;
    // Don't deny access during loading for users with institute_id (they might be superadmins)
    if (accessLoading && user?.institute_id) return true;
    return hasModule(moduleKey);
  };

  // Strict instructor-only permission-based filtering
  const filteredInstructorTrackItems = INSTRUCTOR_TRACK_ITEMS.filter((item) => {
    // For instructor role, check specific permissions
    if (user?.role === 'instructor' && item.permission) {
      return hasPermission(item.permission);
    }

    // Superadmin and admin bypass permission checks for instructor items
    if (user?.role === 'superadmin' || user?.role === 'super_admin' || user?.role === 'admin') {
      return true;
    }

    // For instructors without specific permissions, show items without permission requirements
    if (user?.role === 'instructor' && !item.permission) {
      return true;
    }

    return true;
  });

  // Strict admin-only permission-based filtering
  const filteredStudentTrackItems = STUDENT_TRACK_ITEMS.filter((item) => {
    // Explicitly hide 'Course List' from superadmins
    if (item.name === 'Course List' && isPlatformSuperadmin) {
      return false;
    }

    // For Course List, check institute settings
    if (item.name === 'Course List' && user?.role === 'admin' && user?.institute_id) {
      // Must have both permission AND institute setting enabled
      const hasPermissionCheck = hasPermission(item.permission);
      const instituteSettingEnabled = instituteSettings.showPredefinedCourses;
      return hasPermissionCheck && instituteSettingEnabled;
    }

    // Check module access first (for all roles)
    const moduleCheck = canSeeModule(moduleByHref[item.href]);

    // For admin role only (not superadmin), also check specific permissions
    // Super admin bypasses all permission checks
    // Mentor and Student bypass permission checks for now
    if (user?.role === 'admin' && !isPlatformSuperadmin && item.permission) {
      return moduleCheck && hasPermission(item.permission);
    }

    return moduleCheck;
  });

  const canSeeInstituteAnalytics = !isInstituteAdmin || canSeeModule('analytics');
  const canSeeBranding = !isInstituteAdmin || canSeeModule('branding');
  const filteredInstituteItems = INSTITUTE_ADMIN_ITEMS.filter((item) => {
    if (item.href === '/institute/analytics') return canSeeInstituteAnalytics;
    if (item.href === '/institute/branding') return canSeeBranding;
    return true;
  });
  const adminTailNavigation = isInstituteAdmin
    ? adminNavigation.slice(1).filter((item) => item.href !== '/admin-panel')
    : adminNavigation.slice(1);

  const visibleStudentManagementSubItems = STUDENT_MANAGEMENT_SUBITEMS.filter((item) => {
    if (item.name === 'Registered Users') {
      return isPlatformSuperadmin;
    }
    if (item.permission) {
      return hasPermission(item.permission);
    }
    return true;
  });

  const visibleExamManagementSubItems = EXAM_MANAGEMENT_SUBITEMS.filter((item) => {
    if (item.name === 'Registered Exam Users Marks') {
      return isPlatformSuperadmin;
    }
    if (item.permission) {
      return hasPermission(item.permission);
    }
    return true;
  });

  const renderNavTooltip = (name, children) => {
    if (!isCollapsed) return children;
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={14} className="bg-slate-900 border-none text-white shadow-xl font-black uppercase tracking-widest text-[10px] z-[100]">
          {name}
        </TooltipContent>
      </Tooltip>
    );
  };

  const instructorRoutePrefixes = ['/instructor', '/curriculum-plan', '/assignments'];
  const instructorRoutePaths = ['/code-lab', '/code-lab-basic', '/instructor/assignments', '/profile'];
  const shouldRenderInstructorNav = user?.role === 'instructor' && (
    instructorRoutePrefixes.some(prefix => location.pathname.startsWith(prefix)) ||
    instructorRoutePaths.includes(location.pathname) ||
    location.pathname === '/instructor/dashboard'
  );

  const navRef = useRef(null);

  // Restore scroll position on mount or when dropdowns change (after DOM updates)
  useLayoutEffect(() => {
    const savedScroll = sessionStorage.getItem('sidebarScrollTop');
    const container = navRef.current;
    
    if (savedScroll && container) {
      // User had saved scroll position - restore it
      container.scrollTop = Number(savedScroll);
    } else if (container) {
      // No saved position - scroll to active item after dropdowns have expanded
      const activeEl = container.querySelector('[data-active="true"]');
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [studentManagementOpen, examManagementOpen, paymentsOpen, supportOpen, systemAdministrationOpen]);

  // Save scroll position before unload
  useEffect(() => {
    let container = null;
    const handleScroll = () => {
      if (container) {
        sessionStorage.setItem('sidebarScrollTop', container.scrollTop);
      }
    };
    const handleBeforeUnload = () => {
      if (container) {
        sessionStorage.setItem('sidebarScrollTop', container.scrollTop);
      }
    };

    if (navRef.current) {
      container = navRef.current;
      container.addEventListener('scroll', handleScroll);
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const navigate = useNavigate();

  const [isHoverPreviewOpen, setIsHoverPreviewOpen] = useState(false);
  const hoverTimeoutRef = useRef(null);

  // Close hover preview on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsHoverPreviewOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Clean up hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnterContainer = () => {
    if (!isCollapsed) return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHoverPreviewOpen(true);
  };

  const handleMouseLeaveContainer = () => {
    if (!isCollapsed) return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHoverPreviewOpen(false);
    }, 190);
  };

  const navigationConfig = useMemo(() => {
    const role = user?.role;

    if (role === 'student') {
      const studentGroups = [
        {
          id: 'overview',
          title: 'Overview',
          items: [
            { name: 'Dashboard', href: '/dashboard', icon: Home },
          ],
        },
        {
          id: 'learning',
          title: 'Learning',
          items: [
            { name: 'Courses', href: '/courses', icon: BookOpen },
            { name: 'Live Classes', href: '/live-classes', icon: Video },
          ],
        },
        {
          id: 'assessments',
          title: 'Assessments',
          items: [
            { name: 'Tests / Quiz', href: '/tests', icon: ClipboardList, count: studentPendingCounts.exams },
            { name: 'Topic Exams', href: '/assignments', icon: CheckSquare, count: studentPendingCounts.assignments },
            { name: 'Assignments', href: '/student/assessments', icon: Calendar, count: studentPendingCounts.assessments },
          ],
        },
        {
          id: 'workspace',
          title: 'Workspace',
          items: [
            { name: 'Code Lab', href: '/code-lab', icon: Code2 },
            { name: 'Bookmarks', href: '/bookmarks', icon: Bookmark },
          ],
        },
      ];

      const filteredGroups = user?.type === 'paid_exam_user'
        ? [{
            id: 'assessments',
            title: 'Assessments',
            items: [{ name: 'Tests / Quiz', href: '/tests', icon: ClipboardList, count: studentPendingCounts.exams }]
          }]
        : studentGroups;

      const categories = [
        { id: 'overview', name: 'Overview', icon: Home },
        { id: 'learning', name: 'Learning', icon: BookOpen },
        { id: 'assessments', name: 'Assessments', icon: ClipboardList },
        { id: 'workspace', name: 'Workspace', icon: Code2 },
      ].filter(cat => filteredGroups.some(g => g.id === cat.id));

      return {
        kicker: 'STUDENT SPACE',
        groups: filteredGroups,
        categories,
      };
    }

    if (role === 'instructor') {
      const instructorGroups = [
        {
          id: 'overview',
          title: 'Overview',
          items: [
            { name: t('sidebar.instructorDashboard') || 'Mentor Dashboard', href: '/instructor/dashboard', icon: LayoutDashboard },
          ],
        },
        {
          id: 'teaching',
          title: 'Academic Management',
          items: filteredInstructorTrackItems.filter(item =>
            ['/instructor/courses', '/instructor/programs', '/instructor/runways', '/instructor/batches'].includes(item.href)
          ),
        },
        {
          id: 'students',
          title: 'Students & Attendance',
          items: filteredInstructorTrackItems.filter(item =>
            ['/instructor/students', '/instructor/attendance', '/instructor/attendance-history'].includes(item.href)
          ),
        },
        {
          id: 'evaluations',
          title: 'Assessments & Tools',
          items: filteredInstructorTrackItems.filter(item =>
            ['/instructor/assessments', '/instructor/assignments/portal', '/instructor/question-manager', '/instructor/recordings', '/code-lab'].includes(item.href)
          ),
        },
      ].filter(group => group.items.length > 0);

      const categoryIcons = {
        overview: Home,
        teaching: BookOpen,
        students: Users,
        evaluations: ClipboardList,
      };

      const categoryLabels = {
        overview: 'Overview',
        teaching: 'Teaching',
        students: 'Students',
        evaluations: 'Evaluations',
      };

      const categories = instructorGroups.map(g => ({
        id: g.id,
        name: categoryLabels[g.id] || g.title,
        icon: categoryIcons[g.id] || LayoutDashboard,
      }));

      return {
        kicker: 'MENTOR SPACE',
        groups: instructorGroups,
        categories,
      };
    }

    if (role === 'support') {
      const supportGroups = [
        {
          id: 'support',
          title: 'User Management',
          items: SUPPORT_ITEMS,
        },
      ];

      return {
        kicker: 'SUPPORT SPACE',
        groups: supportGroups,
        categories: [
          { id: 'support', name: 'Support', icon: Users },
        ],
      };
    }

    // Role is Admin or Super Admin
    const isSuperAdminRole = role === 'superadmin' || role === 'super_admin' || isPlatformSuperadmin;

    // Overview Group
    const overviewItems = [
      { name: t('dashboard') || 'Admin Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
    ];
    if (canSeeInstituteAnalytics && isInstituteAdmin) {
      overviewItems.push({ name: t('sidebar.instituteAnalytics') || 'Institute Analytics', href: '/institute/analytics', icon: BarChart3 });
    }
    overviewItems.push({ name: 'Platform Dashboard', href: '/student-track/platform-dashboard', icon: BarChart2 });

    // Academics Group
    const academicHrefs = [
      '/student-track/course-management',
      '/admin/programs',
      '/admin/runways',
      '/student-track/schedule-management',
      '/admin/course-list',
    ];
    const academicItems = filteredStudentTrackItems.filter(item => academicHrefs.includes(item.href));

    // People Group
    const peopleItems = [];
    visibleStudentManagementSubItems.forEach(item => {
      peopleItems.push(item);
    });
    const mentorItem = filteredStudentTrackItems.find(item => item.href === '/student-track/instructor-management');
    if (mentorItem && !peopleItems.some(i => i.href === mentorItem.href)) {
      peopleItems.splice(1, 0, mentorItem);
    }
    if (isSuperAdminRole) {
      USER_MANAGEMENT.forEach(item => {
        if (!peopleItems.some(i => i.href === item.href)) {
          peopleItems.push(item);
        }
      });
    }

    // Evaluations Group
    const evaluationItems = [];
    const evalHrefs = [
      '/admin/assessments',
      '/admin/question-manager',
      '/admin/recording-management',
    ];
    filteredStudentTrackItems.forEach(item => {
      if (evalHrefs.includes(item.href)) {
        evaluationItems.push(item);
      }
    });
    visibleExamManagementSubItems.forEach(item => {
      if (!evaluationItems.some(i => i.href === item.href)) {
        evaluationItems.push(item);
      }
    });

    // Operations & Settings Group
    const operationItems = [];
    filteredStudentTrackItems.forEach(item => {
      if (['/admin/attendance', '/admin/attendance-history'].includes(item.href)) {
        operationItems.push(item);
      }
    });
    ADMIN_PAYMENTS_ITEMS.forEach(item => {
      const superadminOnlyItems = [
        t('sidebar.paymentManagement'),
        t('sidebar.studentPaymentSupport'),
        'Subscription Plans',
        'Referral Settings',
        'Subscriptions',
      ];
      const instituteAdminOnlyItems = [
        'Subscription Payments',
      ];
      if (superadminOnlyItems.includes(item.name) && !isSuperAdminRole) return;
      if (instituteAdminOnlyItems.includes(item.name) && isSuperAdminRole) return;
      operationItems.push(item);
    });
    if (isInstituteAdmin && canSeeBranding) {
      operationItems.push({ name: t('sidebar.branding') || 'Branding', href: '/institute/branding', icon: Palette });
    }
    if (isSuperAdminRole) {
      operationItems.push({ name: 'Email Templates', href: '/superadmin/email-templates', icon: Mail });
    }
    operationItems.push({ name: t('settings.settings') || 'Settings', href: '/settings', icon: Settings });

    const adminGroups = [
      { id: 'overview', title: 'Overview', items: overviewItems },
      { id: 'academics', title: 'Academic Management', items: academicItems },
      { id: 'people', title: 'People Management', items: peopleItems },
      { id: 'evaluations', title: 'Assessments & Media', items: evaluationItems },
      { id: 'operations', title: 'Operations & Settings', items: operationItems },
    ].filter(group => group.items.length > 0);

    const categoryIcons = {
      overview: Home,
      academics: BookOpen,
      people: Users,
      evaluations: ClipboardList,
      operations: Settings,
    };

    const categoryLabels = {
      overview: 'Overview',
      academics: 'Academics',
      people: 'People',
      evaluations: 'Assessments',
      operations: 'Operations',
    };

    const categories = adminGroups.map(g => ({
      id: g.id,
      name: categoryLabels[g.id] || g.title,
      icon: categoryIcons[g.id] || LayoutDashboard,
    }));

    return {
      kicker: isSuperAdminRole ? 'SUPERADMIN CONSOLE' : (isInstituteAdmin ? 'INSTITUTE CONSOLE' : 'ADMIN CONSOLE'),
      groups: adminGroups,
      categories,
    };
  }, [
    user?.role,
    user?.type,
    isPlatformSuperadmin,
    isInstituteAdmin,
    canSeeInstituteAnalytics,
    canSeeBranding,
    filteredInstructorTrackItems,
    filteredStudentTrackItems,
    visibleStudentManagementSubItems,
    visibleExamManagementSubItems,
    studentPendingCounts,
    t,
  ]);

  const { kicker, groups, categories } = navigationConfig;

  const isLinkActive = (href) => {
    if (location.pathname === href) {
      return true;
    }
    const activeParentMenu = sessionStorage.getItem('activeParentMenu');
    if (activeParentMenu && href === activeParentMenu) {
      return true;
    }
    const pathname = location.pathname;

    if (href === '/instructor/question-manager' && (
      pathname.startsWith('/instructor/questions') ||
      (pathname.startsWith('/instructor/assignments/') && pathname.includes('/questions/'))
    )) {
      return true;
    }

    if (href === '/admin/question-manager' && (
      pathname.startsWith('/admin/questions') ||
      (pathname.startsWith('/admin/assignments/') && pathname.includes('/questions/'))
    )) {
      return true;
    }

    if (href === '/instructor/assignments/portal' && pathname.startsWith('/instructor/assignments/')) {
      return true;
    }

    if (href === '/admin/assignments/portal' && pathname.startsWith('/admin/assignments/')) {
      return true;
    }

    if (href === '/instructor/exam-report' && pathname.startsWith('/instructor/exam-report')) {
      return true;
    }

    if (href !== '/dashboard' && href !== '/admin/dashboard' && href !== '/instructor/dashboard' && pathname.startsWith(href + '/')) {
      return true;
    }

    return false;
  };

  const activeCategory = useMemo(() => {
    for (const group of groups) {
      if (group.items.some(item => isLinkActive(item.href))) {
        return group.id;
      }
    }
    return groups[0]?.id || 'overview';
  }, [groups, location.pathname]);

  const handleCategoryClick = (categoryId) => {
    if (isCollapsed) {
      if (setIsCollapsed) setIsCollapsed(false);
      setIsHoverPreviewOpen(false);
    }
    setTimeout(() => {
      const el = document.querySelector(`[data-nav-group="${categoryId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  const handleCategoryHover = (categoryId) => {
    if (isCollapsed) {
      setIsHoverPreviewOpen(true);
      setTimeout(() => {
        const el = document.querySelector(`[data-flyout-group="${categoryId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
    }
  };

  const renderDarkRail = (onLinkClick) => (
    <aside className="v16-category-rail h-full flex flex-col items-center py-4 shrink-0 z-40 select-none relative" aria-label="Navigation categories">
      {/* Top Rail Expand Toggle */}
      <button
        type="button"
        onClick={() => {
          if (setIsCollapsed) setIsCollapsed(false);
          setIsHoverPreviewOpen(false);
        }}
        className="v16-rail-toggle"
        title="Expand navigation sidebar"
        aria-label="Expand navigation sidebar"
        aria-expanded={false}
      >
        <ChevronRight className="h-5 w-5 stroke-[2.2]" />
      </button>

      {/* Rail Separator */}
      <span className="v16-rail-separator" aria-hidden="true" />

      {/* Category buttons */}
      <div className="v16-rail-groups" role="group" aria-label="Navigation groups">
        <TooltipProvider delayDuration={0}>
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isSelected = activeCategory === cat.id;

            return (
              <Tooltip key={cat.id}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => handleCategoryClick(cat.id)}
                    onMouseEnter={() => handleCategoryHover(cat.id)}
                    onFocus={() => handleCategoryHover(cat.id)}
                    className={cn(
                      "v16-category-button",
                      isSelected && "is-selected"
                    )}
                    aria-label={cat.name}
                    aria-pressed={isSelected}
                    title={cat.name}
                  >
                    <Icon className="h-5 w-5 stroke-[2]" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={12} className="bg-[#17254b] text-white text-xs px-2.5 py-1 rounded-lg border-0 shadow-lg">
                  {cat.name}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </TooltipProvider>
      </div>

      {/* Collapsed Rail Bottom Actions */}
      <div className="pt-3 mt-auto flex flex-col items-center gap-2 w-full border-t border-white/10 pb-4">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => {
                  navigate('/profile');
                  if (onLinkClick) onLinkClick();
                  setIsHoverPreviewOpen(false);
                }}
                className="w-10 h-10 rounded-xl flex items-center justify-center text-[#aebede] hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                aria-label="Support & Profile"
                title="Support & Profile"
              >
                <HelpCircle className="h-4.5 w-4.5 stroke-[2]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={12} className="bg-[#17254b] text-white text-xs px-2.5 py-1 rounded-lg border-0 shadow-lg">
              Support
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => {
                  handleLogout();
                  setIsHoverPreviewOpen(false);
                }}
                className="w-10 h-10 rounded-xl flex items-center justify-center text-[#aebede] hover:text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                aria-label="Logout"
                title="Logout"
              >
                <LogOut className="h-4.5 w-4.5 stroke-[2]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={12} className="bg-[#17254b] text-white text-xs px-2.5 py-1 rounded-lg border-0 shadow-lg">
              Logout
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </aside>
  );

  const renderWhitePanel = (isDrawer = false, isFlyout = false) => (
    <div className="v16-context-panel">
      {/* Panel Header */}
      <div className="v16-context-header">
        <div>
          <span className="v16-context-kicker">{kicker}</span>
          <h2>Navigation</h2>
        </div>

        {isDrawer ? (
          <button
            type="button"
            onClick={onNavItemClick}
            className="v16-context-pin"
            title="Close navigation drawer"
            aria-label="Close navigation drawer"
          >
            <X className="h-4 w-4 stroke-[2.2]" />
          </button>
        ) : isFlyout ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => {
                if (setIsCollapsed) setIsCollapsed(false);
                setIsHoverPreviewOpen(false);
              }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-[#3855F6]/30 bg-[#EEF2FF] text-[#3855F6] hover:bg-[#3855F6] hover:text-white text-[11px] font-bold shadow-2xs transition-all hover:scale-105 active:scale-95 cursor-pointer"
              title="Pin navigation sidebar open"
              aria-label="Pin navigation sidebar open"
            >
              <Pin className="h-3 w-3 stroke-[2.5]" />
              <span>Pin</span>
            </button>
            <button
              type="button"
              onClick={() => setIsHoverPreviewOpen(false)}
              className="v16-context-pin"
              title="Close preview"
              aria-label="Close preview"
            >
              <X className="h-3.5 w-3.5 stroke-[2.2]" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsCollapsed && setIsCollapsed(true)}
            className="v16-context-pin hidden lg:flex"
            title="Collapse navigation sidebar (Unpin)"
            aria-label="Collapse navigation sidebar (Unpin)"
            aria-expanded={true}
          >
            <ChevronLeft className="h-4 w-4 stroke-[2.4]" />
          </button>
        )}
      </div>

      {/* Mobile quick category pills (in mobile drawer) */}
      {isDrawer && (
        <div className="grid grid-cols-2 gap-1.5 p-3 pb-1 border-b border-[#edf0f7]">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => {
                const el = document.querySelector(`[data-nav-group="${cat.id}"]`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }}
              className={cn(
                "py-1.5 px-2 rounded-lg text-[11px] font-bold transition-all text-center",
                activeCategory === cat.id
                  ? "bg-[#eaf0ff] text-[#315af0] border border-[#d2ddff]"
                  : "bg-[#f7f9fd] text-[#64799e] border border-[#e9edf7] hover:bg-[#edf2ff]"
              )}
            >
              {cat.name}
            </button>
          ))}
        </div>
      )}

      {/* Navigation Items with sleek low-contrast scrollbar */}
      <div className="v16-context-scroll sidebar-scrollbar">
        <nav className="flex flex-col gap-1" aria-label="Portal navigation">
          {groups.map((group) => {
            const isGroupCurrent = activeCategory === group.id;

            return (
              <div
                key={group.id}
                data-nav-group={isFlyout ? undefined : group.id}
                data-flyout-group={isFlyout ? group.id : undefined}
                className={cn(
                  "v16-context-group",
                  isGroupCurrent && "is-current"
                )}
              >
                <div className="v16-context-group-caption">
                  {group.title}
                </div>
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = isLinkActive(item.href);
                    const hasCount = Boolean(item.count && Number(item.count) > 0);

                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={(e) => {
                          if (onNavItemClick) onNavItemClick(e);
                          if (isFlyout) setIsHoverPreviewOpen(false);
                        }}
                        className={cn(
                          "v16-context-link group",
                          active && "is-active"
                        )}
                        aria-current={active ? "page" : undefined}
                      >
                        <Icon
                          className={cn(
                            "h-4 w-4 shrink-0 transition-colors stroke-[2]",
                            active ? "text-white" : "text-[#64748B] group-hover:text-[#0F172A]"
                          )}
                        />
                        <span className="truncate">{item.name}</span>
                        {hasCount && (
                          <span className={cn(
                            "px-1.5 py-0.5 rounded-full text-[10px] font-bold ml-auto",
                            active ? "bg-white/20 text-white" : "bg-[#2563EB]/10 text-[#2563EB]"
                          )}>
                            {item.count}
                          </span>
                        )}
                        {user?.role !== 'student' && (
                          <ChevronRight className={cn(
                            "w-3.5 h-3.5 ml-auto shrink-0 transition-colors",
                            active ? "text-white/80" : "text-slate-400 opacity-60"
                          )} />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Support & Logout Footer */}
      <footer className="v16-context-footer">
        <button
          type="button"
          onClick={() => {
            navigate('/profile');
            if (onNavItemClick) onNavItemClick();
            if (isFlyout) setIsHoverPreviewOpen(false);
          }}
          className="flex items-center gap-2.5 px-3 h-[36px] rounded-lg text-[#52668c] hover:text-[#365cf5] hover:bg-[#f3f6ff] text-[12px] font-semibold transition-colors cursor-pointer"
        >
          <HelpCircle className="h-4 w-4 stroke-[2]" />
          <span>Support</span>
        </button>

        <button
          type="button"
          onClick={() => {
            handleLogout();
            if (isFlyout) setIsHoverPreviewOpen(false);
          }}
          className="logout-btn flex items-center gap-2.5 px-3 h-[36px] rounded-lg text-[#52668c] hover:text-red-600 hover:bg-red-50 text-[12px] font-semibold transition-colors cursor-pointer"
        >
          <LogOut className="h-4 w-4 stroke-[2]" />
          <span>Logout</span>
        </button>
      </footer>
    </div>
  );

  return (
    <>
      {/* Desktop Unified Collapsible Sidebar */}
      <div
        className="hidden lg:flex h-screen sticky top-0 shrink-0 z-40 relative"
        onMouseEnter={handleMouseEnterContainer}
        onMouseLeave={handleMouseLeaveContainer}
      >
        {isCollapsed ? (
          <>
            {renderDarkRail(onNavItemClick)}

            {/* Hover Preview Flyout */}
            <div
              className={cn(
                "absolute left-full top-0 h-full w-[246px] z-50 bg-white shadow-[0_14px_40px_rgba(16,28,57,0.18),0_2px_8px_rgba(16,28,57,0.08)] border-r border-[#E5E9F2] transition-all duration-200 ease-out origin-left",
                isHoverPreviewOpen
                  ? "opacity-100 translate-x-0 visible pointer-events-auto"
                  : "opacity-0 -translate-x-3 invisible pointer-events-none"
              )}
              role="region"
              aria-label="Navigation Preview"
            >
              {renderWhitePanel(false, true)}
            </div>
          </>
        ) : (
          renderWhitePanel(false, false)
        )}
      </div>

      {/* Mobile Slide-Out Drawer */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex h-full shadow-2xl transition-transform duration-300 lg:hidden",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {renderWhitePanel(true, false)}
      </aside>
    </>
  );
};

export default Sidebar;
